import { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Camera, Images, ArrowLeft, Share2, Calendar } from 'lucide-react';
import { Button } from '../components/ui/button';
import { SiteNav } from '../components/home/SiteNav';
import { SiteFooter } from '../components/home/Sections2027b';
import { Lightbox } from '../components/galeria/Lightbox';
import { galeriaApi } from '../lib/api';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL || '';
const fmtData = (iso) => { if (!iso) return ''; const t = new Date(`${iso}T12:00:00`).toLocaleDateString('pt-BR', { weekday: 'long', day: '2-digit', month: '2-digit', year: 'numeric' }); return t.charAt(0).toUpperCase() + t.slice(1); };

const share = async (title) => {
  const url = window.location.href;
  try {
    if (navigator.share) await navigator.share({ title, url });
    else { await navigator.clipboard.writeText(url); toast.success('Link copiado!'); }
  } catch { /* cancelado */ }
};

export const AlbumCard = ({ album }) => (
  <Link to={`/memorias/${album.album_id}`} className="group rounded-2xl overflow-hidden border border-white/10 bg-white/[0.03] hover:border-kairos-orange/40 hover:-translate-y-1 transition-all duration-300 block" data-testid={`album-card-${album.album_id}`}>
    <div className="aspect-[4/3] bg-obsidian-100 overflow-hidden relative">
      {album.capa_url ? (
        <img src={`${API}${album.capa_url}`} alt={album.titulo} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
      ) : (
        <div className="w-full h-full flex items-center justify-center text-gray-600"><Camera className="w-10 h-10" /></div>
      )}
      <span className="absolute top-3 right-3 px-2.5 py-1 rounded-full bg-black/60 backdrop-blur text-white text-xs flex items-center gap-1"><Images className="w-3 h-3" />{album.total_fotos}</span>
    </div>
    <div className="p-5">
      <h3 className="text-white font-semibold text-lg">{album.titulo}</h3>
      <p className="text-gray-500 text-sm">{fmtData(album.data)}</p>
      {album.descricao && <p className="text-gray-400 text-sm mt-2 line-clamp-2">{album.descricao}</p>}
    </div>
  </Link>
);

export default function MemoriasPage() {
  const { albumId } = useParams();
  const [edicoes, setEdicoes] = useState([]);
  const [album, setAlbum] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lb, setLb] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setLoading(true); setAlbum(null); setNotFound(false);
    const req = albumId ? galeriaApi.albumPublico(albumId).then((r) => setAlbum(r.data)) : galeriaApi.publica().then((r) => setEdicoes(r.data));
    req.catch((err) => { if (err.response?.status === 404) setNotFound(true); else toast.error('Não foi possível carregar a galeria'); }).finally(() => setLoading(false));
    window.scrollTo(0, 0);
  }, [albumId]);

  const title = album ? `${album.titulo} – Memórias do Kairós ${album.edicao?.ano || ''}` : 'Memórias do Kairós – Galeria de fotos';

  return (
    <div className="min-h-screen bg-obsidian" data-testid="memorias-page">
      <Helmet title={title} meta={[{ name: 'description', content: 'Reviva os momentos especiais do Encontro de Casais Kairós 2026: fotos dos quatro dias de aprendizado, emoções e fortalecimento dos laços matrimoniais.' }]} />
      <SiteNav />

      <section className="pt-28 pb-10 relative overflow-hidden">
        <div className="absolute top-0 right-1/4 w-[500px] h-[500px] bg-kairos-orange/10 rounded-full blur-[150px]" />
        <div className="container mx-auto px-4 relative z-10">
          {album ? (
            <>
              <Link to="/memorias" className="inline-flex items-center gap-2 text-gray-400 hover:text-white text-sm mb-6" data-testid="album-back-link"><ArrowLeft className="w-4 h-4" />Todos os álbuns</Link>
              <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
                <div>
                  <p className="text-kairos-orange text-xs font-semibold tracking-[0.2em] mb-3">MEMÓRIAS DO KAIRÓS {album.edicao?.ano}</p>
                  <h1 className="font-display text-4xl sm:text-5xl text-white mb-2" data-testid="album-title">{album.titulo}</h1>
                  <p className="text-gray-400 flex items-center gap-2"><Calendar className="w-4 h-4 text-kairos-orange" />{fmtData(album.data)} • {album.fotos.length} fotos</p>
                  {album.descricao && <p className="text-gray-400 mt-3 max-w-2xl">{album.descricao}</p>}
                </div>
                <Button variant="outline" onClick={() => share(title)} className="rounded-full border-white/20 text-white hover:bg-white/5" data-testid="album-share-btn"><Share2 className="w-4 h-4 mr-2" />Compartilhar álbum</Button>
              </div>
            </>
          ) : (
            <div className="text-center max-w-3xl mx-auto">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-kairos-orange/15 border border-kairos-orange/40 mb-6"><Camera className="w-4 h-4 text-kairos-orange" /><span className="text-kairos-orange text-xs font-semibold tracking-[0.2em]">MEMÓRIAS DO KAIRÓS</span></div>
              <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-white mb-4" data-testid="memorias-title">KAIRÓS 2026 — MOMENTOS QUE <span className="text-kairos-orange">FICARAM PARA SEMPRE</span></h1>
              <p className="text-gray-400 text-base md:text-lg">Quatro dias de aprendizado, emoções, reencontros e fortalecimento dos laços matrimoniais. Reviva alguns dos momentos especiais que fizeram parte desta edição.</p>
            </div>
          )}
        </div>
      </section>

      <section className="pb-24">
        <div className="container mx-auto px-4">
          {loading ? (
            <div className="flex justify-center py-20"><div className="w-10 h-10 border-4 border-kairos-orange border-t-transparent rounded-full animate-spin" /></div>
          ) : album ? (
            album.fotos.length === 0 ? (
              <p className="text-center text-gray-500 py-20" data-testid="album-empty">As fotos deste dia serão publicadas em breve.</p>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="album-grid">
                {album.fotos.map((f, i) => (
                  <button key={f.foto_id} onClick={() => setLb(i)} className="aspect-[4/3] w-full rounded-xl overflow-hidden border border-white/5 hover:border-kairos-orange/40 focus:outline-none focus:ring-2 focus:ring-kairos-orange group bg-obsidian-100" aria-label={`Abrir foto ${i + 1}`} data-testid={`foto-thumb-${i}`}>
                    <img src={`${API}${f.thumb_url}`} alt={f.alt} loading="lazy" width={f.width} height={f.height} className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500" />
                  </button>
                ))}
              </div>
            )
          ) : notFound ? (
            <div className="text-center py-20" data-testid="album-not-found"><p className="text-gray-300 text-lg mb-4">Álbum não encontrado ou ainda não publicado.</p><Link to="/memorias"><Button variant="outline" className="rounded-full border-white/20 text-white">Ver todos os álbuns</Button></Link></div>
          ) : edicoes.length === 0 || edicoes.every((e) => e.albuns.length === 0) ? (
            <p className="text-center text-gray-500 py-20" data-testid="memorias-empty">A galeria será publicada em breve.</p>
          ) : (
            edicoes.map((e) => (
              <div key={e.edicao_id} className="mb-16" data-testid={`edicao-${e.ano}`}>
                {edicoes.length > 1 && <h2 className="font-display text-2xl text-white mb-6">{e.nome}</h2>}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                  {e.albuns.map((a) => <AlbumCard key={a.album_id} album={a} />)}
                </div>
              </div>
            ))
          )}
        </div>
      </section>

      {album && lb !== null && <Lightbox fotos={album.fotos} index={lb} onClose={() => setLb(null)} onIndex={setLb} />}
      <SiteFooter />
    </div>
  );
}
